import {
  memo,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
  MouseEventHandler,
  ReactNode,
  isValidElement,
} from 'react'
import { motion, Transition } from 'framer-motion'
import { ListChecks, X, Zap } from 'lucide-react'
import { Interactive } from '@highlight/ui'
import { HighlightAppIcon } from '@renderer/components/Icons/HighlightAppIcon'
import HighlightLogoIcon from '@renderer/components/Icons/HighlightLogoIcon'
import { ToastHighlightIcon, ToastHighlightSuccessIcon, ChatCompleteIcon } from './components/toast/nested/icon'

/* -------------------------------- Constants ------------------------------- */

const HEIGHT = 40
const WIDTH = 380
const DEFAULT_ROUNDNESS = 12
const DURATION_MS = 600
const DURATION_S = DURATION_MS / 1000
const BLUR_RATIO = 0.5
const PILL_PADDING = 10
const HEADER_PADDING_X = 16 // header `p-2` on both sides
const BADGE_SIZE = 20 // `size-5`
const BADGE_GAP = 8 // `gap-2` between badge and title
const MIN_EXPAND_RATIO = 2.25
const SWAP_COLLAPSE_MS = 200
const HEADER_EXIT_MS = DURATION_MS * 0.7

const titleMaxWidth = (hasIcon: boolean) =>
  WIDTH - HEADER_PADDING_X - PILL_PADDING - (hasIcon ? BADGE_SIZE + BADGE_GAP : 0)

const SPRING: Transition = {
  type: 'spring',
  bounce: 0.25,
  duration: DURATION_S,
}

const SPRING_NO_BOUNCE: Transition = {
  ...SPRING,
  bounce: 0,
}

const INSTANT: Transition = { duration: 0 }

/* ---------------------------------- Types --------------------------------- */

interface ToastPillProps {
  id: string
  fill?: string
  title?: string
  headerExtra?: ReactNode
  description?: ReactNode
  position?: 'left' | 'center' | 'right'
  expand?: 'top' | 'bottom'
  icon?: ReactNode
  styles?: { title?: string; description?: string; badge?: string }
  roundness?: number
  exiting?: boolean
  canExpand?: boolean
  refreshKey?: string
  stableHeaderKey?: string
  autoExpandDelayMs?: number
  autoCollapseDelayMs?: number
  keepExpandedOnRefresh?: boolean
  onMouseEnter?: MouseEventHandler
  onMouseLeave?: MouseEventHandler
  onDismiss?: () => void
  onHeaderClick?: () => void
}

interface HeaderLayer {
  key: string
  view: {
    title?: string
    headerExtra?: ReactNode
    icon?: ReactNode
    fill?: string
    styles?: { title?: string; description?: string; badge?: string }
  }
}

/* ----------------------------- Gooey SVG Defs ----------------------------- */

const GooeyDefs = memo(function GooeyDefs({ filterId, blur }: { filterId: string; blur: number }) {
  return (
    <defs>
      <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation={blur} result="blur" />
        <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -10" result="goo" />
        <feComposite in="SourceGraphic" in2="goo" operator="atop" />
      </filter>
    </defs>
  )
})

const SKIP_SUB_ICON_TYPES = new Set([
  HighlightAppIcon,
  HighlightLogoIcon,
  ToastHighlightIcon,
  ToastHighlightSuccessIcon,
  Zap,
  ListChecks,
  ChatCompleteIcon,
])

function shouldSkipSubIcon(node: ReactNode): boolean {
  return isValidElement(node) && SKIP_SUB_ICON_TYPES.has(node.type as never)
}

const badgeSubIcon = (
  <div className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center">
    <div className="bg-foreground text-background flex size-3 shrink-0 items-center justify-center rounded shadow-md">
      <HighlightAppIcon size={8} />
    </div>
  </div>
)

/* -------------------------------- Component ------------------------------- */

export const ToastPill = memo(function ToastPill({
  id,
  fill = 'var(--background)',
  title,
  headerExtra,
  description,
  position = 'left',
  expand = 'bottom',
  icon,
  styles,
  roundness,
  exiting = false,
  canExpand = true,
  refreshKey,
  stableHeaderKey,
  autoExpandDelayMs,
  autoCollapseDelayMs,
  keepExpandedOnRefresh,
  onMouseEnter,
  onMouseLeave,
  onDismiss,
  onHeaderClick,
}: ToastPillProps) {
  const next = useMemo(
    () => ({ title, headerExtra, description, icon, styles, fill }),
    [title, headerExtra, description, icon, styles, fill],
  )

  const [view, setView] = useState(next)
  const [applied, setApplied] = useState(refreshKey)
  const [isExpanded, setIsExpanded] = useState(false)
  const [ready, setReady] = useState(false)
  const [pillWidth, setPillWidth] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)

  const hasDesc = Boolean(view.description)
  const open = hasDesc && isExpanded
  const allowExpand = canExpand
  const headerKey = stableHeaderKey ?? `${view.title ?? ''}`
  const filterId = `toast-gooey-${id}`
  const resolvedRoundness = Math.max(0, roundness ?? DEFAULT_ROUNDNESS)
  const blur = resolvedRoundness * BLUR_RATIO

  const headerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const headerExitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoExpandRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoCollapseRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const swapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastRefreshKeyRef = useRef(refreshKey)
  const pendingRef = useRef<{ key: string; payload: typeof next } | null>(null)
  const hoveredRef = useRef(false)

  const autoExpandRanRef = useRef<string | undefined>(undefined)
  const collapseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const [headerLayer, setHeaderLayer] = useState<{
    current: HeaderLayer
    prev: HeaderLayer | null
  }>({
    current: { key: headerKey, view },
    prev: null,
  })

  /* ------------------------------ Measurements ------------------------------ */

  const innerRef = useRef<HTMLDivElement>(null)
  const headerPadRef = useRef<number | null>(null)
  const pillRoRef = useRef<ResizeObserver | null>(null)
  const pillRafRef = useRef(0)
  const pillObservedRef = useRef<Element | null>(null)

  useLayoutEffect(() => {
    const el = innerRef.current
    const header = headerRef.current
    if (!el || !header) return

    if (headerPadRef.current === null) {
      const cs = getComputedStyle(header)
      headerPadRef.current = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
    }

    const px = headerPadRef.current
    const measure = () => {
      const w = Math.min(el.scrollWidth + px + PILL_PADDING, WIDTH)
      if (w > PILL_PADDING) {
        setPillWidth((prev) => (prev === w ? prev : w))
      }
    }

    measure()

    if (!pillRoRef.current) {
      pillRoRef.current = new ResizeObserver(() => {
        cancelAnimationFrame(pillRafRef.current)
        pillRafRef.current = requestAnimationFrame(() => {
          const inner = innerRef.current
          const pad = headerPadRef.current ?? 0
          if (!inner) return
          const w = Math.min(inner.scrollWidth + pad + PILL_PADDING, WIDTH)
          if (w > PILL_PADDING) {
            setPillWidth((prev) => (prev === w ? prev : w))
          }
        })
      })
    }

    if (pillObservedRef.current !== el) {
      if (pillObservedRef.current) pillRoRef.current.unobserve(pillObservedRef.current)
      pillRoRef.current.observe(el)
      pillObservedRef.current = el
    }
  }, [headerLayer.current.key])

  useEffect(() => {
    return () => {
      cancelAnimationFrame(pillRafRef.current)
      pillRoRef.current?.disconnect()
    }
  }, [])

  useLayoutEffect(() => {
    if (!hasDesc) {
      setContentHeight(0)
      return
    }

    const el = contentRef.current
    if (!el) return

    const measure = () => {
      const h = el.scrollHeight
      setContentHeight((prev) => (prev === h ? prev : h))
    }

    measure()

    let rafId = 0
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(measure)
    })
    ro.observe(el)
    return () => {
      cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [hasDesc, open])

  /* --------------------------------- Ready --------------------------------- */

  useEffect(() => {
    const raf = requestAnimationFrame(() => setReady(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  /* ----------------------------- Header layer ------------------------------ */

  useLayoutEffect(() => {
    setHeaderLayer((state) => {
      if (state.current.key === headerKey) {
        if (state.current.view === view) return state
        return { ...state, current: { key: headerKey, view } }
      }
      return { prev: state.current, current: { key: headerKey, view } }
    })
  }, [headerKey, view])

  useEffect(() => {
    if (!headerLayer.prev) return
    if (headerExitRef.current) clearTimeout(headerExitRef.current)
    headerExitRef.current = setTimeout(() => {
      headerExitRef.current = null
      setHeaderLayer((state) => ({ ...state, prev: null }))
    }, HEADER_EXIT_MS)
    return () => {
      if (headerExitRef.current) {
        clearTimeout(headerExitRef.current)
        headerExitRef.current = null
      }
    }
  }, [headerLayer.prev])

  /* ----------------------------- Refresh logic ------------------------------ */

  useEffect(() => {
    if (refreshKey === undefined) {
      setView(next)
      setApplied(undefined)
      pendingRef.current = null
      lastRefreshKeyRef.current = refreshKey
      return
    }

    if (lastRefreshKeyRef.current === refreshKey) return
    lastRefreshKeyRef.current = refreshKey

    if (swapTimerRef.current) {
      clearTimeout(swapTimerRef.current)
      swapTimerRef.current = null
    }

    if (open && keepExpandedOnRefresh) {
      pendingRef.current = null
      setView(next)
      setApplied(refreshKey)
    } else if (open) {
      pendingRef.current = { key: refreshKey, payload: next }
      setIsExpanded(false)
      swapTimerRef.current = setTimeout(() => {
        swapTimerRef.current = null
        const pending = pendingRef.current
        if (!pending) return
        setView(pending.payload)
        setApplied(pending.key)
        pendingRef.current = null
      }, SWAP_COLLAPSE_MS)
    } else {
      pendingRef.current = null
      setView(next)
      setApplied(refreshKey)
    }
  }, [open, refreshKey, next, keepExpandedOnRefresh])

  /* ----------------------------- Auto expand/collapse ----------------------- */

  useEffect(() => {
    if (!hasDesc) return
    if (autoExpandRef.current) clearTimeout(autoExpandRef.current)
    if (autoCollapseRef.current) clearTimeout(autoCollapseRef.current)

    if (exiting || !allowExpand) {
      setIsExpanded(false)
      return
    }

    if (autoExpandDelayMs == null && autoCollapseDelayMs == null) return

    // Don't re-trigger if the cycle already ran for this content
    if (autoExpandRanRef.current === applied) return
    autoExpandRanRef.current = applied

    const expandDelay = autoExpandDelayMs ?? 0
    const collapseDelay = autoCollapseDelayMs ?? 0

    if (expandDelay > 0) {
      autoExpandRef.current = setTimeout(() => setIsExpanded(true), expandDelay)
    } else {
      setIsExpanded(true)
    }

    if (collapseDelay > 0) {
      autoCollapseRef.current = setTimeout(() => {
        if (!hoveredRef.current) setIsExpanded(false)
      }, collapseDelay)
    }

    return () => {
      if (autoExpandRef.current) clearTimeout(autoExpandRef.current)
      if (autoCollapseRef.current) clearTimeout(autoCollapseRef.current)
    }
  }, [autoCollapseDelayMs, autoExpandDelayMs, hasDesc, allowExpand, exiting, applied])

  /* ------------------------------ Derived values ---------------------------- */

  const minExpanded = HEIGHT * MIN_EXPAND_RATIO
  const rawExpanded = hasDesc ? Math.max(minExpanded, HEIGHT + contentHeight) : minExpanded
  const frozenExpandedRef = useRef(rawExpanded)
  if (open) frozenExpandedRef.current = rawExpanded

  const expanded = open ? rawExpanded : frozenExpandedRef.current
  const svgHeight = hasDesc ? Math.max(expanded, minExpanded) : HEIGHT
  const expandedContent = Math.max(0, expanded - HEIGHT)
  const resolvedPillWidth = Math.max(pillWidth || HEIGHT, HEIGHT)
  const pillHeight = HEIGHT + blur * 3
  const pillX =
    position === 'right' ? WIDTH - resolvedPillWidth : position === 'center' ? (WIDTH - resolvedPillWidth) / 2 : 0

  /* ------------------------------- Animate targets ------------------------- */

  const pillAnimate = useMemo(
    () => ({
      x: pillX,
      width: resolvedPillWidth,
      height: open ? pillHeight : HEIGHT,
    }),
    [pillX, resolvedPillWidth, open, pillHeight],
  )

  const bodyAnimate = useMemo(
    () => ({ height: open ? expandedContent : 0, opacity: open ? 1 : 0 }),
    [open, expandedContent],
  )

  const bodyTransition = open ? SPRING : SPRING_NO_BOUNCE
  const pillTransition = ready ? SPRING : INSTANT

  const viewBox = `0 0 ${WIDTH} ${svgHeight}`

  const canvasStyle = useMemo(
    () => ({
      filter: `url(#${filterId}) drop-shadow(0 1px 1px rgba(0,0,0,0.08)) drop-shadow(0 3px 3px rgba(0,0,0,0.08)) drop-shadow(0 6px 6px rgba(0,0,0,0.08)) drop-shadow(0 12px 12px rgba(0,0,0,0.06))`,
    }),
    [filterId],
  )

  /* ------------------------------- Inline styles ---------------------------- */

  const rootStyle = useMemo(
    () => ({
      '--_h': `${open ? expanded : HEIGHT}px`,
      '--_pw': `${resolvedPillWidth}px`,
      '--_px': `${pillX}px`,
      '--_ht': `translateY(${open ? (expand === 'bottom' ? 3 : -3) : 0}px) scale(${open ? 0.97 : 1})`,
      '--_co': `${open ? 1 : 0}`,
    }),
    [open, expanded, resolvedPillWidth, pillX, expand],
  )

  /* -------------------------------- Handlers -------------------------------- */

  const handleEnter: MouseEventHandler = useCallback(
    (e) => {
      hoveredRef.current = true
      if (collapseDebounceRef.current) {
        clearTimeout(collapseDebounceRef.current)
        collapseDebounceRef.current = null
      }
      onMouseEnter?.(e)
      if (autoCollapseRef.current) {
        clearTimeout(autoCollapseRef.current)
        autoCollapseRef.current = null
      }

      if (hasDesc) setIsExpanded(true)
    },
    [hasDesc, onMouseEnter],
  )

  const handleLeave: MouseEventHandler = useCallback(
    (e) => {
      hoveredRef.current = false
      onMouseLeave?.(e)
      if (collapseDebounceRef.current) clearTimeout(collapseDebounceRef.current)
      collapseDebounceRef.current = setTimeout(() => {
        collapseDebounceRef.current = null
        if (!hoveredRef.current) setIsExpanded(false)
      }, 80)
    },
    [onMouseLeave],
  )

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.propertyName !== 'height' && e.propertyName !== 'transform') return
      if (open) return
      const pending = pendingRef.current
      if (!pending) return
      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current)
        swapTimerRef.current = null
      }
      setView(pending.payload)
      setApplied(pending.key)
      pendingRef.current = null
    },
    [open],
  )

  /* --------------------------------- Render --------------------------------- */

  return (
    <div
      ref={buttonRef}
      data-toast-pill=""
      data-ready={ready}
      data-expanded={open}
      data-exiting={exiting}
      data-edge={expand}
      data-position={position}
      style={rootStyle as React.CSSProperties}
      onTransitionEnd={handleTransitionEnd}
      className="pointer-events-none relative scale-95 cursor-pointer touch-none overflow-visible border-0 bg-transparent p-0 opacity-0"
    >
      {/* SVG Canvas */}
      <div
        data-toast-canvas=""
        data-edge={expand}
        className="pointer-events-none absolute left-0 right-0 overflow-visible"
        style={canvasStyle}
      >
        <svg data-toast-svg="" width={WIDTH} height={svgHeight} viewBox={viewBox}>
          <title>Notification</title>
          <GooeyDefs filterId={filterId} blur={blur} />
          <motion.rect
            data-toast-pill-rect=""
            rx={resolvedRoundness}
            ry={resolvedRoundness}
            fill={view.fill}
            initial={false}
            animate={pillAnimate}
            transition={pillTransition}
          />
          <motion.rect
            data-toast-body-rect=""
            y={HEIGHT}
            width={WIDTH}
            rx={resolvedRoundness}
            ry={resolvedRoundness}
            fill={view.fill}
            initial={false}
            animate={bodyAnimate}
            transition={bodyTransition}
          />
        </svg>
      </div>

      {/* Header hover surface — static, never transforms */}
      <div
        data-toast-header-hover=""
        data-edge={expand}
        className="pointer-events-auto absolute z-20"
        onClick={onHeaderClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Interactive>
          <span className="sr-only pointer-events-none" />
        </Interactive>
      </div>

      {/* Header — animated, pointer-events-none so it doesn't flicker */}
      <div
        ref={headerRef}
        data-toast-header=""
        data-edge={expand}
        className="pointer-events-none absolute z-20 flex items-center overflow-hidden p-2"
      >
        <div data-toast-header-stack="" className="pointer-events-none inline-flex h-full items-center">
          <div
            ref={innerRef}
            data-toast-header-inner=""
            data-layer="current"
            className="flex items-center gap-2 whitespace-nowrap"
            key={headerLayer.current.key}
          >
            {headerLayer.current.view.icon && (
              <div className="relative shrink-0">
                <div
                  data-toast-badge=""
                  className={`bg-background flex size-5 shrink-0 items-center justify-center rounded-[6px] shadow-md ${headerLayer.current.view.styles?.badge ?? ''}`}
                >
                  {headerLayer.current.view.icon}
                </div>
                {!shouldSkipSubIcon(headerLayer.current.view.icon) && badgeSubIcon}
              </div>
            )}
            <span
              data-toast-title=""
              style={{ maxWidth: titleMaxWidth(Boolean(headerLayer.current.view.icon)) }}
              className={`text-foreground truncate text-[0.825rem] font-medium leading-4 ${headerLayer.current.view.styles?.title ?? ''}`}
            >
              {headerLayer.current.view.title}
            </span>
            {headerLayer.current.view.headerExtra}
          </div>
          {headerLayer.prev && (
            <div
              data-toast-header-inner=""
              data-layer="prev"
              data-exiting="true"
              className="pointer-events-none absolute left-0 top-0 z-0 flex items-center gap-2 whitespace-nowrap"
              key={headerLayer.prev.key}
            >
              {headerLayer.prev.view.icon && (
                <div className="relative shrink-0">
                  <div
                    data-toast-badge=""
                    className={`bg-background flex size-5 shrink-0 items-center justify-center rounded-[6px] shadow-md ${headerLayer.prev.view.styles?.badge ?? ''}`}
                  >
                    {headerLayer.prev.view.icon}
                  </div>
                  {!shouldSkipSubIcon(headerLayer.prev.view.icon) && badgeSubIcon}
                </div>
              )}
              <span
                data-toast-title=""
                style={{ maxWidth: titleMaxWidth(Boolean(headerLayer.prev.view.icon)) }}
                className={`text-foreground truncate text-[0.825rem] font-medium leading-4 ${headerLayer.prev.view.styles?.title ?? ''}`}
              >
                {headerLayer.prev.view.title}
              </span>
              {headerLayer.prev.view.headerExtra}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {hasDesc && (
        <div
          data-toast-content=""
          data-edge={expand}
          data-visible={open}
          className="pointer-events-none absolute left-0 z-10 w-full"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {open && (
            <Interactive>
              <span className="sr-only pointer-events-none" />
            </Interactive>
          )}
          <div
            ref={contentRef}
            data-toast-description=""
            className={`text-foreground type-prose-sm w-full p-4 text-left ${view.styles?.description ?? ''}`}
          >
            {view.description}
          </div>
        </div>
      )}

      {/* Close */}
      {onDismiss && (
        <div
          data-toast-close-wrap=""
          className="absolute -top-2 z-30 size-5"
          style={{
            left:
              position === 'center' && expand === 'bottom'
                ? `calc(var(--_px) - 8px)`
                : position !== 'right'
                  ? -8
                  : undefined,
            right: position === 'right' ? -8 : undefined,
          }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <Interactive>
            <button
              type="button"
              data-toast-close=""
              className="bg-background text-foreground pointer-events-none relative flex size-5 cursor-pointer items-center justify-center rounded-full p-0 opacity-0 shadow-sm transition-opacity duration-150"
              tabIndex={0}
              aria-label="Close"
              onClick={(e) => {
                e.stopPropagation()
                onDismiss()
              }}
            >
              <X size={14} />
            </button>
          </Interactive>
        </div>
      )}
    </div>
  )
})
